import { TslDialogRef, TSL_DIALOG_DATA } from '@tesla/ng-tds';
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, retry } from 'rxjs/operators';
import {
    Grid, GridOptions,
    ValueFormatterParams,
    ValueGetterParams,
    ITooltipParams,
    GridReadyEvent,
    ColumnResizedEvent,
    ColDef,
    ICellRendererParams
} from 'ag-grid-community';
import { AppSettings } from 'app/app.settings';
import * as agGridLib from '@tesla/ag-grid-lib';
import { DatePipe } from '@angular/common';
import { UserPermissions } from 'app/app.permissions';
import { AuthData, AuthStorage } from '@tesla/ng-auth';
import { ProfileService } from 'app/profile/services/profile.service';
import { removeDuplicates, isEqual } from 'app/shared/utils/utils';
import { GridService } from 'app/shared/services/grid.service';
import { OPERATION, ActionFlags } from 'app/profile/models/supplier.model';
import { StageStatus, StageProperty, Tab } from 'app/profile/models/stage.model';
import { PascalCasePipe } from 'app/shared/pipes/splitString.pipe';
import { I18nService } from 'app/shared/services/i18n.service';
import * as FileSaver from 'file-saver';
import { SupplierStateService } from 'app/profile/services/supplierstate.service';
import { AttachmentLink } from 'app/shared/ag-grid-components/attachmentLink.renderer';

@Component({
    selector: 'app-tab-history',
    templateUrl: './tab-history.component.html',
    styleUrls: ['./tab-history.component.scss'],
    providers: [PascalCasePipe]
})
export class TabHistoryComponent implements OnInit, AfterViewInit {
    code: string;
    flags: ActionFlags;
    auditGridOptions: GridOptions;
    allowAction = true;
    onboardingCompleted = false;
    tabName: string;
    options = {};
    filter = {
        PageSize: 25,
        skip: 0
    };
    internalColumns = ['stageid', 'id',
        'accountnumber',
        'iban', 'ibancode',
        'methodofpaymentid',
        'contactid', 'addressid',
        'bankid', 'taxinfoid', 'federaltaxid', 'suppliermaps', 'esignagreementid', 'attachment', 'documentid',
        'deactivatedbyuserid', 'owneruserid', 'supportowneruserid', 'attachmentpath', 'language', 'operation', 'ssoid', 'activeinokta'];
    dateColumns = ['modifieddate', 'inactivedate', 'ownerstartdate', 'signeddate', 'effectivedate', 'expirydate', 'uploadeddate'];

    @ViewChild('AuditGrid') notificationGridDiv: ElementRef;
    constructor(
        public dialogRef: MatDialogRef<TabHistoryComponent>,
        @Inject(MAT_DIALOG_DATA) public historyData: any,
        private dialog: MatDialog,
        private service: ProfileService,
        private router: ActivatedRoute,
        private configuration: AppSettings,
        private datepipe: DatePipe,
        private authData: AuthData,
        private authStorage: AuthStorage,
        private gridService: GridService,
        private pascalCase: PascalCasePipe,
        public translate: I18nService,
        private supplierStateService: SupplierStateService,
    ) {

        this.code = historyData.supplierCode;
        this.tabName = historyData.tabName;
        this.options = {
            orderBy: 'CreatedTimeStamp',
            orderByDescending: true
        };
    }

    ngOnInit() {
        this.getKey = this.getKey.bind(this);
    }

    ngAfterViewInit() {
        const filterDefaults: ColDef = {
            filter: false,
            sortable: false,
            enablePivot: false,
            resizable: true,
            cellClass: 'cell-text',
            filterParams: {
                applyButton: true,
                clearButton: true,
                newRowsAction: 'keep'
            }
        };

        const detailGridOptions: GridOptions = {
            columnDefs: [
                { headerName: 'Tab', field: 'type', rowGroup: true, hide: true },
                { headerName: 'Key Change', field: 'key', rowGroup: true, hide: true, width: 900 },
                { headerName: 'Operation', field: 'operation', width: 200 },
                { headerName: 'Field Name', field: 'field', width: 400 },
                { headerName: 'Old Value', field: 'old', width: 400 },
                { headerName: 'New Value', field: 'new', width: 400, autoHeight: true, cellStyle: {'white-space': 'normal', 'word-break': 'break-all'} }
            ],
            defaultColDef: filterDefaults,
            pagination: false,
            context: {
                componentParent: this
            },
            onGridReady: this.onGridReady,
            groupMultiAutoColumn: true
        };

        this.auditGridOptions = {
            components: {
                'AttachmentLink': AttachmentLink
            },
            columnDefs: [
                {
                    headerName: 'Event Name', field: 'name', cellRenderer: 'agGroupCellRenderer', width: 400,
                    enablePivot: false, sortable: false, filter: false
                },
                {
                    headerName: 'Tabs Modified',
                    cellRenderer: (params) => params.data && params.data.properties && params.data.properties.length > 0 ?
                        `${removeDuplicates(
                            this.getDetailData(params.data.properties).map(x => x.type).filter(x => x.toString() !== 'Status')
                        )}` : null,
                    width: 300,
                    enablePivot: false,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Requested By', field: 'createdBy',
                    cellRenderer: (params) => params.data && params.data.createdByUser ?
                        `${params.data.createdByUser.firstName} ${params.data.createdByUser.lastName}` : null,
                    tooltip: (params) => params.data && params.data.createdByUser ? params.data.createdByUser.email :
                        params.data ? params.data.createdBy : null,
                    enablePivot: false,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Requested Date', field: 'createdTimeStamp', valueFormatter: this.DateFormatter,
                    enablePivot: false,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Status', field: 'status', width: 100,
                    enablePivot: false,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Action Date', field: 'modifiedTimeStamp', valueFormatter: this.DateFormatter,
                    enablePivot: false,
                    sortable: false,
                    filter: false
                },
                {
                    headerName: 'Attachments', field: 'attachment',
                    cellRenderer: 'AttachmentLink',
                    autoHeight: true
                },
                {
                    headerName: 'Rejection Reason', field: 'statusDescription',
                    cellRenderer: (params) => params.data && params.data.rejectionJSON && params.data.rejectionJSON.rejections?.length > 0 ?
                        params.data.rejectionJSON.rejections[0].rejectedReasons.join(", ") : null,
                    enablePivot: false,
                    sortable: false,
                    filter: false,
                    autoHeight: true
                },
                {
                    headerName: 'Approval/Rejection Comments',
                    field: 'comment',
                    cellRenderer: (params) => params.data && params.data.rejectionJSON && params.data.rejectionJSON.rejections?.length > 0 ?
                        params.data.rejectionJSON.rejections[0].rejectionComment : null,
                    tooltipField: 'comment',
                    width: 375,
                    enablePivot: false,
                    sortable: false,
                    filter: false
                },
            ],
            rowModelType: 'serverSide',
            cacheBlockSize: this.filter.PageSize,
            maxBlocksInCache: this.filter.PageSize,
            enableCellTextSelection: true,
            pagination: true,
            paginationPageSize: this.filter.PageSize,
            animateRows: true,
            defaultColDef: filterDefaults,
            onGridReady: this.onGridReady,
            onColumnResized: this.onColumnResized,
            embedFullWidthRows: true,
            detailRowHeight: 600,
            sideBar: false,
            context: {
                gridDiv: this.notificationGridDiv.nativeElement,
                componentParent: this
            },
            gridAutoHeight: true,
            masterDetail: true,
            detailCellRendererParams: {
                detailGridOptions: detailGridOptions,
                getDetailRowData: this.transformProperties.bind(this)
            }
        };

        // create the grid passing in the div to use together with the columns & data we want to use
        const _poSearchGrid = new Grid(this.notificationGridDiv.nativeElement, this.auditGridOptions);
        this.gridService.setOptions(this.options);
        const url = `${this.configuration.BaseUrl}Profile/History/${this.code}/${this.historyData.tabName}`;
        const apiServer = new agGridLib.SeedServer(
            this.gridService,
            url,
            this.auditGridOptions,
            this.options
        );
        const datasource = new agGridLib.ServerSideDatasource(apiServer);
        this.auditGridOptions.api.setServerSideDatasource(datasource);
        agGridLib.showPageSize(this.auditGridOptions, this.filter.PageSize);
    }

    showAction(params: ICellRendererParams) {
        const data = params.data;
        if (data) {
            return this.allowAction &&
                data.status === StageStatus.Requested &&
                this.authData.hasPermission(data.approverRoles.toLowerCase());
        }

        return false;
    }

    onGridReady(params: GridReadyEvent) {
        (params.api as any).context.beanWrappers.tooltipManager.beanInstance.MOUSEOVER_SHOW_TOOLTIP_TIMEOUT = 0;
    }

    onColumnResized(params: ColumnResizedEvent) {
        params.api.resetRowHeights();
    }
    transformProperties(params) {
        const properties = params.data && params.data.properties ? params.data.properties : null;
        if (properties) {
            params.successCallback(this.getDetailData(properties));
        } else {
            params.successCallback(null);
        }
    }

    getDetailData(properties: StageProperty[]) {
        return properties.map((property: StageProperty) => {
            // Update the case to pascal case for all properties.
            if (property.newValue) {
                property.newValue = this.pascalCase.transform(property.newValue);
            }
            if (property.oldValue) {
                property.oldValue = this.pascalCase.transform(property.oldValue);
            }
            const keyName = this.getKeyName(property);
            const reference = this.getKey(property, keyName);
            let response = null;
            if (property.type==Tab.EHS)
            { 
                response = this.formatEHS(property.newValue["Response"]);
            }
            return Object.keys(property.newValue).map((key: string) => {
                const result = {
                    type: property.type,
                    keyName: keyName,
                    key: reference,
                    field: key,
                    operation: property.newValue.operation || 'Update',
                    new: (property.type==Tab.EHS && key=="Response")? response: property.newValue[key],
                    old: property.oldValue ? property.oldValue[key] : null
                };
                if (this.dateColumns.indexOf(result.field.toLowerCase()) > -1) {
                    result.new = result.new ? this.datepipe.transform(result.new, 'medium') : null;
                    result.old = result.old ? this.datepipe.transform(result.old, 'medium') : null;
                }
                return result;
            }).filter(x => {
                if (x.operation === OPERATION.DELETE) {
                    return isEqual(x.key, x.new);
                } else {
                    return !isEqual(x.new, x.old) && this.internalColumns.indexOf(x.field.toLowerCase()) === -1;
                }
            });
        }).reduce(function (a, b) {
            return a.concat(b);
        });
    }

    getKeyName(data: StageProperty) {
        if (!data) { return null; }
        switch (data.type) {
            case Tab.Contact:
                return 'Email';
            case Tab.Address:
                return 'Types';
            case Tab.Bank:
                return 'BankNickName';
            case Tab.Tax:
                return 'TaxName';
            case Tab.Document:
                return 'DocumentType';
            case Tab.Entity:
                return 'EntityName';
            default:
                return null;
        }
    }

    getKey(data: StageProperty, keyName: string) {
        if (!data) { return null; }
        if (data.oldValue) {
            return data.oldValue[keyName];
        }
        if (data.newValue) {
            return data.newValue[keyName];
        }
        return null;
    }

    refresh() {
        this.auditGridOptions.api.purgeServerSideCache();
    }

    DateFormatter(params: ValueFormatterParams) {
        return params.context.componentParent.datepipe.transform(params.value, 'medium');
    }

    downloadFile(document: any) {
        if (document != null) {
            this.supplierStateService.downloadFile(this.code, document.referenceSystem ? document.referenceSystem : 'DMS', document.documentID, document.documentVersion).subscribe(file => {
                const fileName: string = document.fileName;
                if (file) {
                    FileSaver.saveAs(file, fileName);
                }
            });
        }
    }
    formatEHS(response: any): any {
        // Iterate through each key-value pair in the response object
        const responseJson = JSON.parse(response);
        for (const key in responseJson) {
            if (responseJson.hasOwnProperty(key)) {
                // Check if the key matches the pattern "ehsQ"
                if (responseJson[key] === "" && (key.startsWith('documents')||key.startsWith('response'))) {
                    // Remove the item from the response object
                    delete responseJson[key];
                }
                if (key == "title" || key == "subtitle") { 
                    delete responseJson[key];
                }
                if (key.startsWith('ehsQ')) {
                    // Get the question number from the key
                    const questionNumber = key.substring(3); // Remove "ehsQ" from the key
    
                    // Update the key and value according to your requirement
                    responseJson[`${questionNumber}`] = responseJson[key] === "1" ? "Yes" : "No";
    
                    // Delete the old key if needed
                    delete responseJson[key];
                }
            }
        }
        return JSON.stringify(responseJson);
    }
}
